import { useState, useRef, useEffect } from 'react';
import { similarBooksRules, validateBookSelection, formatBookData, maintainInputFocus } from '../../rules';
import { API_BASE_URL } from '../../config/api';

async function fetchJson(url, timeoutMs = 8000) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await fetch(url, { signal: controller.signal });
    if (!response.ok) {
      throw new Error(`Request failed with status ${response.status}`);
    }
    return response.json();
  } finally {
    clearTimeout(timeoutId);
  }
}

function normalizeGoogleBooks(data) {
  if (!data?.items) return [];

  return data.items
    .filter((item) => item?.id && item?.volumeInfo?.title)
    .map((item) => ({
      id: item.id,
      source: 'google',
      title: item.volumeInfo.title,
      author: item.volumeInfo.authors?.[0] || 'Unknown Author',
      thumbnail: item.volumeInfo.imageLinks?.thumbnail || null,
      ratingsCount: item.volumeInfo.ratingsCount || 0,
      averageRating: item.volumeInfo.averageRating || 0,
      publishedDate: item.volumeInfo.publishedDate,
      publisher: item.volumeInfo.publisher,
      categories: item.volumeInfo.categories || []
    }));
}

function normalizeOpenLibraryBooks(data) {
  if (!data?.docs) return [];

  return data.docs
    .filter((item) => item?.key && item?.title)
    .map((item) => ({
      id: item.key,
      source: 'openLibrary',
      title: item.title,
      author: item.author_name?.[0] || 'Unknown Author',
      thumbnail: item.cover_i ? `https://covers.openlibrary.org/b/id/${item.cover_i}-M.jpg` : null,
      publishedDate: item.first_publish_year?.toString(),
      ratingsCount: item.ratings_count || 0,
      averageRating: item.ratings_average || 0,
      editionCount: item.edition_count || 0
    }));
}

async function searchViaBackend(searchQuery, currentPage) {
  const data = await fetchJson(
    `${API_BASE_URL}/api/books/search?q=${encodeURIComponent(searchQuery)}&page=${currentPage}`,
    12000
  );
  return Array.isArray(data?.books) ? data.books.filter((book) => book?.title) : [];
}

async function searchViaProviders(searchQuery, currentPage) {
  const offset = (currentPage - 1) * 10;
  const apiKey = import.meta.env.VITE_GOOGLE_BOOKS_API_KEY;
  const googleUrl = `https://www.googleapis.com/books/v1/volumes?q=${encodeURIComponent(searchQuery)}&maxResults=10&startIndex=${offset}&langRestrict=en${apiKey ? `&key=${apiKey}` : ''}`;
  const openLibraryUrl = `https://openlibrary.org/search.json?q=${encodeURIComponent(searchQuery)}&limit=10&offset=${offset}&language=eng`;

  const [googleResult, openLibraryResult] = await Promise.allSettled([
    fetchJson(googleUrl),
    fetchJson(openLibraryUrl)
  ]);

  const googleBooks = googleResult.status === 'fulfilled'
    ? normalizeGoogleBooks(googleResult.value)
    : [];
  const openLibraryBooks = openLibraryResult.status === 'fulfilled'
    ? normalizeOpenLibraryBooks(openLibraryResult.value)
    : [];

  return {
    books: [...googleBooks, ...openLibraryBooks],
    failed: googleResult.status === 'rejected' && openLibraryResult.status === 'rejected'
  };
}

function BookSearch({ selectedBooks = [], onBookSelect, onBookRemove, mode = 'similar' }) {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [selectedIndex, setSelectedIndex] = useState(-1); // Track keyboard navigation
  const searchTimeout = useRef(null);
  const suggestionsRef = useRef(null);
  const inputRef = useRef(null);

  // Reset selected index when suggestions change
  useEffect(() => {
    setSelectedIndex(-1);
  }, [suggestions]);

  // Effect to maintain focus on input when suggestions appear
  useEffect(() => {
    if (showSuggestions && similarBooksRules.searchBehavior.keepFocusOnSearch) {
      maintainInputFocus(inputRef);
    }
  }, [showSuggestions, suggestions]);

  // Add a new effect to maintain focus during loading state changes
  useEffect(() => {
    if (similarBooksRules.searchBehavior.keepFocusOnSearch) {
      maintainInputFocus(inputRef);
    }
  }, [isLoading]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Levenshtein distance calculation for fuzzy matching
  const levenshteinDistance = (str1, str2) => {
    const track = Array(str2.length + 1).fill(null).map(() =>
      Array(str1.length + 1).fill(null));
    for (let i = 0; i <= str1.length; i += 1) {
      track[0][i] = i;
    }
    for (let j = 0; j <= str2.length; j += 1) {
      track[j][0] = j;
    }
    for (let j = 1; j <= str2.length; j += 1) {
      for (let i = 1; i <= str1.length; i += 1) {
        const indicator = str1[i - 1] === str2[j - 1] ? 0 : 1;
        track[j][i] = Math.min(
          track[j][i - 1] + 1, // deletion
          track[j - 1][i] + 1, // insertion
          track[j - 1][i - 1] + indicator, // substitution
        );
      }
    }
    return track[str2.length][str1.length];
  };

  // Calculate similarity score between two strings
  const calculateSimilarity = (str1, str2) => {
    if (!str1 || !str2) return 0;
    const maxLength = Math.max(str1.length, str2.length);
    if (maxLength === 0) return 1.0;
    const distance = levenshteinDistance(str1.toLowerCase(), str2.toLowerCase());
    return 1 - distance / maxLength;
  };

  const searchBooks = async (searchQuery, currentPage = 1, append = false) => {
    // Don't search if query is too short
    if (!searchQuery.trim() || (searchQuery.trim().length < similarBooksRules.searchBehavior.minQueryLength)) {
      setSuggestions([]);
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      let allBooks = [];
      try {
        allBooks = await searchViaBackend(searchQuery, currentPage);
      } catch (backendError) {
        console.warn('Backend book search failed, falling back to providers:', backendError);
        const providerResult = await searchViaProviders(searchQuery, currentPage);
        allBooks = providerResult.books;
        if (providerResult.failed) {
          throw backendError;
        }
      }
      const uniqueBooks = allBooks.filter((book) => book?.title).reduce((acc, current) => {
        const x = acc.find(item => {
          const titleSimilarity = calculateSimilarity(item.title, current.title);
          const authorSimilarity = calculateSimilarity(item.author, current.author);
          return titleSimilarity > 0.85 && authorSimilarity > 0.7;
        });
        if (!x) {
          return [...acc, current];
        }
        // If duplicate found, prefer Google Books data but keep OpenLibrary ratings if better
        if (current.source === 'google' && x.source === 'openLibrary') {
          return acc.map(item => {
            if (item.title === x.title) {
              return {
                ...current,
                ratingsCount: Math.max(current.ratingsCount, x.ratingsCount),
                averageRating: current.ratingsCount > x.ratingsCount ? current.averageRating : x.averageRating
              };
            }
            return item;
          });
        }
        return acc;
      }, []);

      // Enhanced scoring system
      const scoredBooks = uniqueBooks
        .map(book => {
          let score = 0;
          const lowerTitle = book.title.toLowerCase();
          const lowerQuery = searchQuery.toLowerCase();
          
          // Title similarity scoring
          const titleSimilarity = calculateSimilarity(book.title, searchQuery);
          score += titleSimilarity * 1000;
          
          // Exact matches get highest priority
          if (lowerTitle === lowerQuery) {
            score += 3000;
          }
          
          // Word-by-word matching with position bonus
          const searchTerms = searchQuery.toLowerCase().split(' ');
          const titleWords = book.title.toLowerCase().split(' ');
          
          searchTerms.forEach((term, termIndex) => {
            titleWords.forEach((word, wordIndex) => {
              const wordSimilarity = calculateSimilarity(term, word);
              if (wordSimilarity > 0.8) {
                score += (wordSimilarity * 100) * (1 + (titleWords.length - wordIndex) / titleWords.length);
              }
            });
          });
          
          // Metadata scoring
          if (book.ratingsCount) {
            score += Math.min(Math.log10(book.ratingsCount + 1) * 20, 100);
          }
          if (book.averageRating) {
            score += book.averageRating * 20;
          }
          if (book.publishedDate) {
            const year = parseInt(book.publishedDate);
            if (year && year > 2000) score += 20;
            if (year && year > 2015) score += 10;
          }
          if (book.editionCount) {
            score += Math.min(Math.log10(book.editionCount + 1) * 10, 30);
          }
          if (book.categories?.length > 0) {
            score += 10;
          }
          
          return { ...book, score };
        })
        .sort((a, b) => b.score - a.score)
        .slice(0, 10);

      // Either append to existing results or set new results
      setSuggestions(prev => append ? [...prev, ...scoredBooks] : scoredBooks);
    } catch (error) {
      console.error('Error fetching books:', error);
      setError(similarBooksRules.errors.searchError);
      if (!append) {
        setSuggestions([]);
      }
    } finally {
      setIsLoading(false);
      // Maintain focus after search completes
      maintainInputFocus(inputRef);
    }
  };

  const handleLoadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    searchBooks(query, nextPage, true);
  };

  // New function to handle search button click
  const handleSearchClick = () => {
    if (query.trim().length >= similarBooksRules.searchBehavior.minQueryLength) {
      setShowSuggestions(true);
      searchBooks(query, 1, false);
      setPage(1);
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setQuery(value);
    
    // Clear suggestions when query is too short
    if (value.trim().length < similarBooksRules.searchBehavior.minQueryLength) {
      setShowSuggestions(false);
      setSuggestions([]);
      
      // Clear any existing search timeout
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current);
      }
    } else {
      // Only show suggestions if we have enough characters, but don't search immediately
      // This provides a fallback for users who expect the old behavior
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current);
      }
      
      // Use a longer debounce time for the fallback auto-search
      searchTimeout.current = setTimeout(() => {
        setShowSuggestions(true);
        searchBooks(value, 1, false);
      }, 2000); // 2 seconds instead of 500ms to reduce API calls
    }
    
    setPage(1); // Reset page when query changes
  };

  const handleSelectBook = (book) => {
    if (selectedBooks.length < similarBooksRules.maxBooks) {
      onBookSelect(book);
      setQuery('');
      setSuggestions([]);
      setShowSuggestions(false);
      setError('');
      // Maintain focus after selection
      maintainInputFocus(inputRef);
    } else {
      setError(similarBooksRules.errors.tooManyBooks);
    }
  };

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    // Handle Enter key for search
    if (e.key === 'Enter') {
      e.preventDefault();
      if (query.trim().length >= similarBooksRules.searchBehavior.minQueryLength) {
        handleSearchClick();
      }
      return;
    }

    // Only handle keyboard navigation when suggestions are shown
    if (!showSuggestions || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault(); // Prevent cursor from moving in input
        setSelectedIndex(prev => 
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault(); // Prevent cursor from moving in input
        setSelectedIndex(prev => (prev > 0 ? prev - 1 : prev));
        break;
      case 'Escape':
        e.preventDefault();
        setShowSuggestions(false);
        break;
      default:
        break;
    }
  };

  return (
    <div className="w-full space-y-4">
      {/* Search input with button */}
      <div className="relative" ref={suggestionsRef}>
        <div className="relative flex">
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder={similarBooksRules.placeholders.searchInput}
            className="flex-1 px-4 py-3 bg-white border border-gray-200 rounded-l-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
            disabled={isLoading}
            onFocus={() => {
              // Only show suggestions if we have enough characters
              if (query.trim().length >= similarBooksRules.searchBehavior.minQueryLength) {
                setShowSuggestions(true);
              }
            }}
          />
          <button
            onClick={handleSearchClick}
            disabled={isLoading || query.trim().length < similarBooksRules.searchBehavior.minQueryLength}
            className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-r-xl font-medium hover:from-blue-700 hover:to-purple-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-sm hover:shadow border-l-0 border border-gray-200"
          >
            {isLoading ? (
              <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
            ) : (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            )}
          </button>
        </div>

        {/* Suggestions dropdown - only show if we have enough characters */}
        {showSuggestions && query.trim().length >= similarBooksRules.searchBehavior.minQueryLength && (
          <div 
            className="absolute z-50 left-0 right-0 mt-1 w-full bg-white rounded-xl border border-gray-200 shadow-lg max-h-[400px] overflow-y-auto"
            onClick={(e) => {
              // Prevent clicks on the dropdown from stealing focus
              e.stopPropagation();
            }}
            // Prevent mousedown events to ensure focus is not lost when clicking in the dropdown
            onMouseDown={(e) => {
              e.preventDefault();
            }}
          >
            {isLoading && suggestions.length === 0 ? (
              <div className="p-4 text-center text-gray-600">
                <div className="inline-block animate-spin rounded-full h-5 w-5 border-2 border-purple-600 border-t-transparent mr-2"></div>
                <span>{similarBooksRules.loadingStates.searching}</span>
              </div>
            ) : suggestions.length > 0 ? (
              <>
                {suggestions.map((book, index) => (
                  <button
                    key={book.id}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSelectBook(book);
                    }}
                    className={`w-full p-4 flex items-center gap-4 hover:bg-gray-50 transition-colors text-left border-b last:border-b-0 ${
                      index === selectedIndex ? 'bg-purple-50' : ''
                    }`}
                    onMouseEnter={() => setSelectedIndex(index)}
                  >
                    {book.thumbnail && (
                      <img src={book.thumbnail} alt={book.title} className="w-12 h-16 object-cover rounded" />
                    )}
                    <div>
                      <div className="font-medium text-gray-900 line-clamp-2">{book.title}</div>
                      <div className="text-sm text-gray-500">{book.author}</div>
                    </div>
                  </button>
                ))}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleLoadMore();
                  }}
                  onMouseDown={(e) => {
                    e.preventDefault(); // Prevent focus loss on mousedown
                  }}
                  disabled={isLoading}
                  className="w-full p-3 flex items-center justify-center gap-2 text-sm text-purple-600 hover:text-purple-700 hover:bg-gray-50 transition-colors border-t disabled:opacity-50"
                >
                  {isLoading ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-purple-600 border-t-transparent"></div>
                  ) : (
                    <>
                      <span>Show more results</span>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </>
                  )}
                </button>
              </>
            ) : (
              <div className="p-4 text-center text-gray-600">
                <p>{similarBooksRules.placeholders.emptyState}</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Selected books */}
      <div className="flex flex-wrap gap-2">
        {selectedBooks.map((book) => (
          <div
            key={book.id}
            className="flex items-center gap-2 p-2 pr-3 bg-purple-50 rounded-full border border-purple-100"
          >
            {book.thumbnail && (
              <img src={book.thumbnail} alt={book.title} className="w-8 h-8 rounded-full object-cover" />
            )}
            <span className="text-sm font-medium text-gray-900">{book.title}</span>
            <button
              onClick={() => onBookRemove(book.id)}
              className="text-gray-400 hover:text-gray-600"
              disabled={isLoading}
            >
              ×
            </button>
          </div>
        ))}
      </div>

      {/* Error message */}
      {error && (
        <div className="text-red-500 text-sm text-center">{error}</div>
      )}

      {/* Helper text */}
      {selectedBooks.length < similarBooksRules.maxBooks && !isLoading && (
        <p className="text-sm text-gray-500 text-center">
          Select up to {similarBooksRules.maxBooks - selectedBooks.length} more book{similarBooksRules.maxBooks - selectedBooks.length !== 1 ? 's' : ''}
        </p>
      )}
    </div>
  );
}

export default BookSearch; 